import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LinearUnit } from '@trimble-gcs/common';
import { ProjectSettings } from 'trimble-connect-workspace-api';
import { SetProjectSettings } from '../app-state/app.actions';
import { Scan3dStyle } from './models/scan-3d-style';
import { Scan3dStyleUom } from './models/scan-3d-style-uom';
import { SetGlobalStyle } from './scan-3d.actions';

export interface Scan3dStateModel {
  globalStyle?: Scan3dStyle;
  scan3dStyleUom: Scan3dStyleUom;
}

const defaultScan3dStyleUom: Scan3dStyleUom = {
  lengthUnit: LinearUnit.Meter,
  lengthDecimals: 2,
  lengthFractions: 2,
};

@State<Scan3dStateModel>({
  name: 'scan3dState',
  defaults: {
    scan3dStyleUom: defaultScan3dStyleUom,
  },
})
@Injectable()
export class Scan3dState {
  @Selector() static globalStyle(state: Scan3dStateModel): Scan3dStyle | undefined {
    return state.globalStyle;
  }

  @Selector() static scan3dStyleUom(state: Scan3dStateModel): Scan3dStyleUom {
    return state.scan3dStyleUom;
  }

  @Action(SetGlobalStyle) setGlobalStyle(
    ctx: StateContext<Scan3dStateModel>,
    { style }: SetGlobalStyle,
  ) {
    ctx.patchState({ globalStyle: style });
  }

  @Action(SetProjectSettings)
  setProjectSettings(ctx: StateContext<Scan3dStateModel>, action: SetProjectSettings) {
    ctx.patchState({
      scan3dStyleUom: this.mapProjectSettingsToScan3dStyleUom(
        action.projectSettings,
        defaultScan3dStyleUom,
      ),
    });
  }

  private mapProjectSettingsToScan3dStyleUom(
    projectSettings: ProjectSettings,
    defaultScan3dStyleUom: Scan3dStyleUom,
  ): Scan3dStyleUom {
    const formatting = projectSettings.formatting;

    const lengthUnit =
      formatting?.lengthFormatting === 'fractional'
        ? LinearUnit.FeetInch
        : (formatting?.lengthUnit ?? defaultScan3dStyleUom.lengthUnit);

    const formattingLengthDecimals =
      formatting?.lengthDecimals ?? defaultScan3dStyleUom.lengthDecimals;
    const lengthDecimals =
      formatting?.lengthFormatting === 'fractional' && formattingLengthDecimals < 0
        ? defaultScan3dStyleUom.lengthDecimals
        : formattingLengthDecimals;

    return {
      lengthUnit,
      lengthDecimals,
      lengthFractions: formatting?.lengthFractions ?? defaultScan3dStyleUom.lengthFractions,
    };
  }
}
